import { Box, IconButton, ListItem, TextField, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import cx from 'classnames';
import React from 'react';
import { SortableHandle as sortableHandle } from 'react-sortable-hoc';
import { VerticalEllipsisIcon } from '../../assets/Svg';
import { IStory, StoryType } from '../../services/types';
import { getMatchedSeason } from '../../services/utils';
import { CopyToClipboard } from '../CopyToClipboard';
import { TextstoryIcon, ChatstoryIcon, AudiobookIcon, WebcomicIcon } from '../../assets/Svg';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { GeoIndicator } from '../GeoIndicator';

export interface IStoryItemBaseProps {
  readonly?: boolean;
  sortable?: boolean;
  removable?: boolean;

  onItemClick?: (story: IStory) => void;
  onStoryGenreTextChange?: (story: IStory, text: string) => void;
  onRemoveClick?: (story: IStory) => void;

  additionalInfo?: boolean;
  className?: string;
}

type IStoryItemProps = IStoryItemBaseProps & {
  story: IStory;
  idx?: number;
  extraText?: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    fontSize: 13,
    color: theme.palette.text.primary,
    paddingBottom: 1,
    display: 'flex',
    alignItems: 'center',
    // opacity: 0.54,
  },

  storyGenreTextInput: { fontSize: 13 },

  storyGenreTextColor: {
    color: theme.palette.primary.main,
  },

  defaultGenreColor: {
    color: '#777',
  },

  dragButton: {
    marginTop: '9px',
  },

  dragItem: {
    paddingLeft: '0 !Important',
  },

  label: {
    color: '#fff',
    height: 14,
    paddingLeft: 4,
    paddingRight: 4,
    fontSize: 9,
    textAlign: 'center',
    marginRight: 5,
  },

  storyLabel: {
    backgroundColor: '#ff008a',
  },

  seasonLabel: {
    backgroundColor: 'grey',
  },
}));

const DragHandle = sortableHandle(() => <VerticalEllipsisIcon />);

interface IconOptions {
  color?: string;
  width?: number;
  height?: number;
  style?: CSSProperties;
  key?: string;
}
export const contentTypeIcon = (storyType: StoryType | undefined, options?: IconOptions): JSX.Element | null => {
  switch (storyType) {
    case StoryType.Text:
      return <TextstoryIcon key={options?.key} color={options?.color} width={options?.width} height={options?.height} style={options?.style} />;
    case StoryType.Audio:
      return <AudiobookIcon key={options?.key} color={options?.color} width={options?.width} height={options?.height} style={options?.style} />;
    case StoryType.WebComic:
      return <WebcomicIcon key={options?.key} color={options?.color} width={options?.width} height={options?.height} style={options?.style} />;
    case StoryType.Chat:
      return <ChatstoryIcon key={options?.key} color={options?.color} width={options?.width} height={options?.height} style={options?.style} />;
    default:
      return null;
  }
};

const StoryItem = (props: IStoryItemProps) => {
  const classes = useStyles();
  const { story, extraText, readonly, sortable, removable, onItemClick, onStoryGenreTextChange, onRemoveClick, additionalInfo } = props;
  const season = getMatchedSeason(story);

  return (
    <ListItem
      alignItems="flex-start"
      style={{ paddingTop: '4px', paddingBottom: '4px', paddingLeft: '8px', paddingRight: '8px' }}
      button={true}
      onClick={() => {
        onItemClick && onItemClick(story);
      }}
      key={`${story.id}`}
      className={!readonly && sortable ? classes.dragItem : undefined}
    >
      {!readonly && sortable && (
        <span className={classes.dragButton}>
          <DragHandle />
        </span>
      )}
      <Box display="flex" style={{ width: '100%' }}>
        <img
          src={season && season.coverUrl ? `${season.coverUrl}?w=150` : `${process.env.CDN_URL}/static/cms/storycover-empty-v.png`}
          style={{
            minWidth: 35,
            width: 35,
            height: 54,
            marginTop: 2,
            marginRight: 12,
            objectFit: 'cover',
          }}
        />
        <Box flexDirection="column" width={'100%'}>
          <Typography className={classes.title}>
            <span className={cx(classes.label, story.seasonId ? classes.seasonLabel : classes.storyLabel)}>
              {story.seasonId ? 'SEASON' : 'STORY'}
            </span>
            {contentTypeIcon(story.type)}
            {story.seasonId ? (story.isCustomSeasonTitle ? season!.title : `${story.title} (S.${season!.no})`) : story.title}
          </Typography>
          <Typography
            style={{
              fontSize: 13,
              color: '#777',
              paddingBottom: 1,
            }}
          >
            <GeoIndicator text={story.availableCountryCountText} storyId={story.id} seasonId={story.seasonId} /> {story.penname && story.penname.name}
          </Typography>
          {extraText && <Typography variant="caption">{extraText}</Typography>}
          {additionalInfo && readonly && (
            <Typography
              style={{
                fontSize: 13,
                color: '#777',
              }}
            >
              <CopyToClipboard content={story.seasonId ? story.seasonId : story.id} />/
              {
                <span className={story.storyGenreText ? classes.storyGenreTextColor : classes.defaultGenreColor}>
                  {story.storyGenreText || story.themeText}
                </span>
              }
            </Typography>
          )}
          {!readonly && (
            <TextField
              placeholder="Collection - Story Genre or Theme"
              fullWidth
              defaultValue={story.storyGenreText}
              InputProps={{
                classes: {
                  input: classes.storyGenreTextInput,
                },
              }}
              onBlur={e => {
                const text = e.target.value;
                onStoryGenreTextChange && onStoryGenreTextChange(story, text);
              }}
            />
          )}
        </Box>
        {!readonly && removable && (
          <IconButton
            style={{
              width: 59,
              height: 59,
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();

              onRemoveClick && onRemoveClick(story);

              return false;
            }}
          >
            <DeleteIcon color="primary" />
          </IconButton>
        )}
      </Box>
    </ListItem>
  );
};

export default StoryItem;
